import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { Subject, takeUntil } from 'rxjs';
import { DataTypes, IConfiguration, IInfoColumn, IStatusColumn } from '../../models/production-plan.models';
import { ProductionPlanConfigurationService } from '../../production-plan-configuration.service';
import { productionPlanValidators } from '../../production-plan.validators';

@Component({
	selector: 'app-edit-configuration',
	templateUrl: './edit-configuration.component.html',
	styleUrls: ['./edit-configuration.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditConfigurationComponent implements OnInit, OnDestroy {
	@Output() updated = new EventEmitter<IConfiguration>();
	private destroy$ = new Subject<void>();

	form: FormGroup;
	isLoading = false;
	httpError = '';
	terminals: ITerminal[] = [];
	plans: IConfiguration[] = [];
	dataTypes: string[] = Object.keys(DataTypes).filter((key) => Number.isNaN(Number(key)));

	configuration: IConfiguration;

	@ViewChildren('infoTitleInput') infoTitleInputs: QueryList<ElementRef<HTMLInputElement>>;

	@ViewChildren('statusTitleInput') statusTitleInputs: QueryList<ElementRef<HTMLInputElement>>;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IConfiguration,
		public dialogRef: MatDialogRef<EditConfigurationComponent>,
		private configurationService: ProductionPlanConfigurationService,
		private terminalService: TerminalAdministrationService,
		private fb: FormBuilder,
	) {
		this.configuration = JSON.parse(JSON.stringify(data));
	}

	ngOnInit(): void {
		this.loadTerminals();
		this.loadPlans();
		this.initializeForm();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadTerminals(): void {
		this.terminalService
			.getTerminals()
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => (this.terminals = response.result.sort((a, b) => a.alias.localeCompare(b.alias))));
	}

	private loadPlans(): void {
		this.configurationService
			.get()
			.pipe(takeUntil(this.destroy$))
			.subscribe((plans) => (this.plans = plans.filter((p) => p.id !== this.configuration.id).sort((a, b) => a.alias.localeCompare(b.alias))));
	}

	private initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.configuration.alias, productionPlanValidators.alias],
			description: [this.configuration.description, productionPlanValidators.description],
			orderPositionSql: [this.configuration.orderPositionSql ?? '', productionPlanValidators.orderPositionSql],
			infoColumns: this.fb.array([], productionPlanValidators.infoColumns),
			statusColumns: this.fb.array([], productionPlanValidators.statusColumns),
		});

		this.populateFormArrays();
	}

	private populateFormArrays(): void {
		if (this.configuration.infoColumns) {
			this.configuration.infoColumns.forEach((col) => {
				this.infoColumns.push(
					this.fb.group({
						title: [col.title, productionPlanValidators.title],
						source: [col.source, productionPlanValidators.source],
						type: [col.type ?? 'String'],
						format: [col.format ?? '', productionPlanValidators.format],
					}),
				);
			});
		}
		if (this.configuration.statusColumns) {
			this.configuration.statusColumns.forEach((col) => {
				this.statusColumns.push(
					this.fb.group({
						title: [col.title, productionPlanValidators.title],
						terminalIds: [col.terminalIds || []],
						planLinksIds: [col.planLinksIds || []],
					}),
				);
			});
		}
	}

	get controls() {
		return this.form.controls;
	}

	get infoColumns(): FormArray<FormGroup> {
		return this.form.get('infoColumns') as FormArray<FormGroup>;
	}

	get statusColumns(): FormArray<FormGroup> {
		return this.form.get('statusColumns') as FormArray<FormGroup>;
	}

	getFirstValidationErrorName(name: string): string {
		return Object.keys(this.form.controls[name].errors)[0];
	}

	addInfoColumn(): void {
		this.infoColumns.push(
			this.fb.group({
				title: ['', productionPlanValidators.title],
				source: ['', productionPlanValidators.source],
				type: ['String'],
				format: ['', productionPlanValidators.format],
			}),
		);
		setTimeout(() => this.infoTitleInputs?.last?.nativeElement?.focus());
	}

	deleteInfoColumn(index: number): void {
		this.infoColumns.removeAt(index);
	}

	addStatusColumn(): void {
		this.statusColumns.push(
			this.fb.group({
				title: ['', productionPlanValidators.title],
				terminalIds: [[]],
				planLinksIds: [[]],
			}),
		);
		setTimeout(() => this.statusTitleInputs?.last?.nativeElement?.focus());
	}

	deleteStatusColumn(index: number): void {
		this.statusColumns.removeAt(index);
	}

	onDropInfoColumn(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) return;
		const moved = this.infoColumns.at(event.previousIndex);
		this.infoColumns.removeAt(event.previousIndex);
		this.infoColumns.insert(event.currentIndex, moved);
	}

	onDropStatusColumn(event: CdkDragDrop<AbstractControl[]>): void {
		if (event.previousIndex === event.currentIndex) return;
		const moved = this.statusColumns.at(event.previousIndex);
		this.statusColumns.removeAt(event.previousIndex);
		this.statusColumns.insert(event.currentIndex, moved);
	}

	onCancel = () => this.dialogRef.close();

	onSave(): void {
		if (this.isLoading || this.form.invalid) return;
		this.httpError = '';
		this.isLoading = true;

		const updated: IConfiguration = {
			id: this.configuration.id,
			alias: this.form.value.alias,
			description: this.form.value.description,
			orderPositionSql: this.form.value.orderPositionSql,
			infoColumns: this.form.value.infoColumns as IInfoColumn[],
			statusColumns: this.form.value.statusColumns as IStatusColumn[],
		};

		this.configurationService
			.update(updated)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.updated.emit(result);
					this.dialogRef.close(result);
				},
				error: (err) => {
					this.httpError = `${err.status}`;
					this.isLoading = false;
				},
			});
	}
}
